import { gql } from '@apollo/client';

/**
 * RITA-1523 (Nutzungserfassung, Versionsvergleich) — zwei Stände nebeneinander.
 *
 * VIERTES Dokument der Lese-API neben GetUsageInsights, GetUsageErrors und GetUsageClickMap. Es
 * steht für sich, obwohl es am gleichen Fenster hängt: seine beiden Kennungen folgen erst aus der
 * Ständeliste (Alias `versions` im Bündel) und wechseln, wenn der Betreiber die Auswahl ändert —
 * ohne Zeitraumwechsel. Im Bündel würde jede Auswahländerung alle sechzehn Auswahlen des Bündels
 * wiederholen. Die zusätzliche Audit-Zeile je Auswahländerung ist damit bewusst: beide neuen
 * Felder stehen in apps/backend/src/services/audit/sensitiveReadWhitelist.ts, also schreibt jede
 * Anfrage genau eine Read-Zeile. Der Absatz „Lesen der Auswertung" in docs/AUDIT-COVERAGE.md nennt
 * noch zehn Queries und wird im Backend-Nachzug fortgeschrieben (dort liegt auch die Begründung).
 *
 * `versionA` ist der ÄLTERE Stand („Vorher"), `versionB` der jüngere („Nachher"); die Änderung
 * liest sich als Nachher gegen Vorher. Beide Argumente sind Pflicht und werden serverseitig hart
 * auf Form und Länge geprüft (usageRangeArgs.ts `assertAppVersion`, A ≠ B).
 *
 * Gefragt wird JEDES Feld einer Zelle. `suppressed` und null sind nicht dasselbe: suppressed heißt
 * „aus Datenschutzgründen nichts anzeigbar" (auf dieser Seite standen hinter diesem Stand weniger
 * als fünf Personen), null bei einer bestehenden Zelle heißt „nicht gemessen" (usage.graphql,
 * UsageVersionCell). `days` trägt die Sperre der Änderungsspalte: unter drei Tagen im Fenster ist
 * eine Richtung nicht bewertbar. `pagesPerSessionStep` und `avgSessionMs` gibt es NUR in der
 * Gesamtzeile — in einer Seitenzelle sind sie immer null.
 *
 * Absolute Zahlen je Stand (Aufrufe, Personen, Fehlerzahl) hat die Antwort bewusst nicht: sie
 * ließen sich von den Zahlen der Seiten-Tabelle abziehen (CONTRACT 3b V0).
 *
 * QUOTEN ALS STUFE (CONTRACT 3b V0a, Feldname mit „Step"): `errorRateStep`, `frustRateStep`,
 * `bounceRateStep` und `pagesPerSessionStep` sind nicht die Quote, sondern die UNTERE KANTE ihrer
 * Stufe auf einer festen Leiter. Eine exakte Quote wäre rückrechenbar: drei exakte Quoten mit
 * demselben Nenner legen diesen Nenner fest (die Zähler sind ganze Zahlen, und `usageRoutes`
 * veröffentlicht die Aufrufe der Seite über ALLE Stände) — damit wäre die Aufrufzahl je Stand
 * bekannt und die Restmenge „alle übrigen Stände" eine Subtraktion weit. Dasselbe galt für die
 * exakten Seiten je Sitzung gegen den Überblick; die frühere Restunschärfe an dieser Stelle ist
 * damit im BACKEND geschlossen, nicht in der Anzeige. Mediane aus Histogrammen und Mittelwerte über
 * große Summen (`avgSessionMs`, `avgScrollPct`) bleiben exakt: aus ihnen folgt kein Nenner.
 *
 * Deshalb werden `rateLadder` und `pagesPerSessionLadder` MITGEFRAGT: die Oberfläche beschriftet
 * die Stufe als Spanne („2 bis unter 5 je 100 Aufrufe") und liest die Kanten aus DIESER Antwort.
 * Eine eigene Kopie der Leitern im Frontend wäre eine zweite Wahrheit — anders als bei den
 * Histogramm-Kanten (services/eimerLeitern.ts, dort eine Aussage der Oberfläche über die
 * Genauigkeit) gehören diese Kanten zur Zahl selbst.
 */
const GET_USAGE_VERSION_COMPARE = gql`
  fragment UsageVersionCellFields on UsageVersionCell {
    suppressed
    days
    p50VisibleMs
    errorRateStep
    frustRateStep
    bounceRateStep
    avgScrollPct
    lcpP50
    inpP50
    clsP50
    pagesPerSessionStep
    avgSessionMs
  }

  query GetUsageVersionCompare($range: UsageRangeInput!, $versionA: String!, $versionB: String!) {
    usageVersionCompare(range: $range, versionA: $versionA, versionB: $versionB) {
      days
      dataFrom
      dataUntil
      missingDays
      lastRollupAt
      versionA
      versionB
      selectionStale
      total {
        route
        standA {
          ...UsageVersionCellFields
        }
        standB {
          ...UsageVersionCellFields
        }
      }
      rows {
        route
        standA {
          ...UsageVersionCellFields
        }
        standB {
          ...UsageVersionCellFields
        }
      }
      hiddenRoutes
      rateLadder
      pagesPerSessionLadder
    }
  }
`;

export default GET_USAGE_VERSION_COMPARE;
